/**
 * L'unico punto da cui la pagina parla col servizio.
 *
 * **Un solo posto** perche' tre cose devono valere per ogni chiamata e nessuna
 * si puo' lasciare alla memoria di chi scrive la schermata: il gettone, la
 * chiave di idempotenza sulle azioni che scrivono, e la lettura dell'errore
 * nella forma che il servizio usa — `{error, message, request}`.
 *
 * **L'identificatore della richiesta si conserva.** Quando l'operatore segnala
 * un problema, quella stringa e' cio' che permette di ritrovarlo nel giornale
 * fra migliaia di righe.
 */

/** Errore del servizio, gia' letto. Porta il codice, non solo la frase: le
 *  schermate decidono sul codice, l'operatore legge la frase. */
export class ServiceError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
    readonly extra: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ServiceError'
  }
}

const BASE = '/api'

let token: string | null = null

export function useToken(value: string | null): void {
  token = value
}

/** Che cosa fare quando il servizio dice che la sessione non vale piu'.
 *
 * Sta qui e non in ogni schermata: un gettone scaduto si scopre alla prima
 * chiamata qualunque essa sia, e trattarlo in venti punti significa che in
 * diciannove si finisce davanti a una pagina muta. */
let expired: (() => void) | null = null

export function onExpired(handler: (() => void) | null): void {
  expired = handler
}

/** Una chiave per ogni azione davvero distinta. */
export function newKey(): string {
  return crypto.randomUUID()
}

async function respond(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body) headers.set('Content-Type', 'application/json')

  const response = await fetch(`${BASE}${path}`, { ...init, headers })
  const requestId = response.headers.get('X-Request-Id')

  if (response.status === 401) expired?.()

  if (!response.ok) {
    // Il corpo puo' mancare — un servente davanti puo' rispondere prima che il
    // servizio veda la richiesta — e in quel caso l'errore va costruito lo
    // stesso: una schermata che aspetta un JSON che non arriva si blocca senza
    // dire niente.
    const body = (await response.json().catch(() => null)) as Record<string, unknown> | null
    const extra = { ...(body ?? {}) }
    delete extra.error
    delete extra.message
    delete extra.request
    throw new ServiceError(
      response.status,
      typeof body?.error === 'string' ? body.error : 'unreachable',
      typeof body?.message === 'string' ? body.message : 'Il servizio non ha risposto.',
      requestId,
      extra,
    )
  }
  return response
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await respond(path, init)
  // `204` non porta corpo: chiederne uno solleverebbe un errore di lettura
  // proprio sulle azioni riuscite — chiusure e rilasci — cioe' quelle che non
  // hanno niente da raccontare.
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

export function get<T>(path: string): Promise<T> {
  return call<T>(path)
}

/** Toglie qualcosa: una sessione, un lease. Nessuna chiave di idempotenza —
 *  cancellare due volte cio' che non c'e' piu' e' gia' la stessa cosa. */
export function drop(path: string): Promise<void> {
  return call<void>(path, { method: 'DELETE' })
}

/** Toglie qualcosa che il servizio registra come atto — lo spegnimento della
 *  vista del modello — e percio' porta la chiave: il rinvio dopo uno scossone
 *  di rete non deve lasciare due righe nel giornale per un gesto solo. */
export function revoke<T>(path: string, key: string): Promise<T> {
  return call<T>(path, { method: 'DELETE', headers: { 'Idempotency-Key': key } })
}

/** Una domanda che si scrive nel corpo ma non lascia traccia: nessuna chiave,
 *  perche' non c'e' niente da non ripetere. */
export function ask<T>(path: string, body: unknown): Promise<T> {
  return call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) })
}

/** Porta qualcosa a uno stato. Nessuna chiave di idempotenza: portare due volte
 *  la stessa cosa allo stesso stato e' la stessa cosa che portarla una volta, e
 *  una chiave qui chiederebbe alla pagina di distinguere due gesti identici. */
export function patch<T>(path: string, body: unknown): Promise<T> {
  return call<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) })
}

/** Le azioni che scrivono portano sempre la chiave: il rinvio dopo un
 *  scossone di rete non deve produrre un secondo verdetto. */
export function send<T>(path: string, body: unknown, key: string): Promise<T> {
  return call<T>(path, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
    headers: { 'Idempotency-Key': key },
  })
}

/** Scarica un contenuto protetto mantenendo il gettone di sessione. */
export async function download(path: string): Promise<Blob> {
  const response = await respond(path)
  return response.blob()
}
